import React, { useCallback, useEffect, useState, useMemo } from 'react'
import { useSelector } from 'react-redux'
import dayjs from 'dayjs'
import localeData from 'dayjs/plugin/localeData'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronRight, faChevronUp } from '@fortawesome/free-solid-svg-icons'
import clsx from 'clsx'

import SlideToggle from '../../../utils/SlideToggle'
import Label from '../../../utils/Label'

dayjs.extend(localeData)

const Selector = ({ onChange, ...props }) => {
  const sites = useSelector(store => store.availabilityRequests.sites)
  const facility = useSelector(store => store.facilities.facility)
  const [siteStartsWith, setSiteStartsWith] = useState()
  const [specificSiteIds, setSpecificSiteIds] = useState(props.siteIds || [])
  const [loopExpanded, setLoopExpanded] = useState([])

  useEffect(() => {
    if (sites.length > 0) {
      const siteIds = sites.map(site => parseInt(site.id))
      const currentSiteIds = specificSiteIds.filter(d => siteIds.includes(d))
      if (JSON.stringify(currentSiteIds) !== JSON.stringify(specificSiteIds)) {
        setSpecificSiteIds(currentSiteIds)
      }
    }
  }, [sites])

  const toggleSite = siteId => {
    setSpecificSiteIds(
      specificSiteIds.includes(siteId)
        ? specificSiteIds.filter(d => d !== siteId)
        : specificSiteIds.concat([parseInt(siteId)])
    )
  }

  const toggleLoop = loop => {
    if (isFilteringSites) {
      const siteIds = sites.filter(site => site.loop === loop).map(site => parseInt(site.id))
      const loopState = specificSiteIds.filter(x => siteIds.includes(x))

      if (loopState.length > 0) {
        // active so remove
        setSpecificSiteIds(specificSiteIds.filter(d => !siteIds.includes(d)))
      } else {
        // inactive so add
        setSpecificSiteIds(specificSiteIds.concat(siteIds))
      }
    } else {
      // first uncheck, set everything else as active
      const siteIds = sites.filter(site => site.loop !== loop).map(site => parseInt(site.id))
      setSpecificSiteIds(specificSiteIds.concat(siteIds))
    }
  }

  const clearLoop = loop => {
    const siteIds = sites.filter(site => site.loop === loop).map(site => parseInt(site.id))

    setSpecificSiteIds(specificSiteIds.filter(d => !siteIds.includes(d)))
  }

  const toggleLoopExpanded = loop => {
    setLoopExpanded(loopExpanded.includes(loop) ? loopExpanded.filter(d => d !== loop) : loopExpanded.concat([loop]))
  }

  const filteredSites = useCallback(
    loop => {
      if (!siteStartsWith) {
        return sites.filter(site => site.loop === loop)
      }

      return sites.filter(
        site =>
          site.loop === loop &&
          (specificSiteIds.includes(site.id) ||
            site.siteNum.startsWith(siteStartsWith) ||
            site.siteNum.replace(/^0+/, '').startsWith(siteStartsWith))
      )
    },
    [sites, specificSiteIds, siteStartsWith]
  )

  const isFilteringSites = useMemo(() => specificSiteIds.length > 0, [specificSiteIds])
  const matchingSitesCount = useMemo(() => isFilteringSites ? specificSiteIds.length : sites.length, [sites, specificSiteIds, isFilteringSites])

  useEffect(() => onChange(specificSiteIds, matchingSitesCount || 0), [specificSiteIds, matchingSitesCount])

  return (
    <div className="">
      <div className="flex items-end space-x-6 md:space-x-12 border-b-2 border-green-500 pb-4">
        <Label className="flex-shrink-0">Matching Sites</Label>

        <span className="font-semibold text-gray-700 px-2 py-1 bg-green-200 rounded-md">
          {matchingSitesCount}
          <span className="hidden md:inline"> Selected</span>
        </span>

        <input
          className="max-w-sm min-w-0 appearance-none border border-gray-400 rounded py-2 px-3 text-gray-700 leading-tight placeholder-opacity-50 bg-gray-100"
          placeholder="Filter By Site Name"
          type="text"
          onChange={e => setSiteStartsWith(e.target.value)}
        />
      </div>

      {facility.sitesDetails?.loopsArray && (
        <div className="">
          <div className="">
            {facility.sitesDetails.loopsArray.map(loop => (
              <div key={loop.name} className={`${filteredSites(loop.name).length > 0 ? '' : 'hidden'}`}>
                <div className="border-b border-gray-200 pr-1 pb-2 pt-6 cursor-pointer flex items-center ">
                  {facility.sitesDetails.loopsArray.filter(loop => filteredSites(loop.name).length > 0).length > 1 && (
                    <div className="flex-initial">
                      <SlideToggle
                        checked={
                          isFilteringSites
                            ? filteredSites(loop.name).filter(site => specificSiteIds.includes(site.id)).length > 0
                            : true
                        }
                        onClick={() => toggleLoop(loop.name)}
                      />
                    </div>
                  )}

                  <div className="flex-grow" onClick={() => toggleLoopExpanded(loop.name)}>
                    <span className="text-xl mr-6 text-gray-500">{loop.name}</span>
                    <span className="whitespace-nowrap">
                      <span className="text-sm text-green-600">
                        {isFilteringSites
                          ? filteredSites(loop.name).filter(site => specificSiteIds.includes(site.id)).length
                          : filteredSites(loop.name).length}
                      </span>
                      <span className="text-sm text-gray-500"> of {filteredSites(loop.name).length}</span>
                    </span>
                  </div>
                  <div className="flex-initial onClick={() => toggleLoopExpanded(loop)}">
                    <FontAwesomeIcon
                      icon={siteStartsWith || loopExpanded.includes(loop.name) ? faChevronUp : faChevronRight}
                      className="text-4xl text-gray-500 pb-2"
                    />
                  </div>
                </div>

                <div
                  className={`space-x-2-right space-y-2 ${
                    siteStartsWith || loopExpanded.includes(loop.name) ? '' : 'hidden'
                  }`}
                >
                  <div className={clsx('mt-2')}>
                    <span className="text-md font-semibold text-grey-500">
                      Select Specific Sites: [
                      <span className="cursor-pointer" onClick={() => toggleLoop(loop.name)}>
                        All
                      </span>
                      /
                      <span className="cursor-pointer" onClick={() => clearLoop(loop.name)}>
                        Clear
                      </span>
                      ]
                    </span>
                  </div>

                  {filteredSites(loop.name).map(site => (
                    <button
                      type="button"
                      key={site.id}
                      className={`${
                        specificSiteIds.includes(site.id) ? 'bg-green-400' : 'bg-white'
                      } items-center px-3 py-1 rounded-md border`}
                      onClick={() => toggleSite(site.id)}
                    >
                      <span className="text-sm font-bold  text-gray-700">{site.siteNum}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default Selector
